import { format, parseISO, eachDayOfInterval, startOfMonth, endOfMonth } from "date-fns";
import { Lock } from "lucide-react";
import { fmtDateLong, fmtWeekday, fmtMonthShort, fmtTime } from "../lib/dates";

/* ----------------------------------------------------------
 * Timeline (Gantt-style) view
 * Rows = employees, X-axis = hours of the day (0-24).
 * Each shift assignment is rendered as a horizontal bar
 * positioned by its start_time → end_time across the day.
 * Days are stacked vertically so a long roster scrolls.
 * ---------------------------------------------------------- */
export const TimelineRosterView = ({ roster, employees, shifts }) => {
  if (!roster?.start_date || !roster?.end_date) return null;
  const dates = eachDayOfInterval({
    start: parseISO(roster.start_date),
    end: parseISO(roster.end_date),
  });
  const shiftMap = Object.fromEntries(shifts.map((s) => [s.shift_id, s]));
  const minutesOfDay = (t) => {
    const [h, m] = (t || "00:00").split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  const HOURS = Array.from({ length: 25 }, (_, i) => i);

  return (
    <div className="space-y-6" data-testid="layout-timeline-root">
      {dates.map((d) => {
        const ds = format(d, "yyyy-MM-dd");
        return (
          <div key={ds} className="border border-zinc-200 dark:border-zinc-800 rounded overflow-hidden">
            <div className="px-4 py-2 bg-zinc-50 dark:bg-zinc-800/60 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                {fmtDateLong(d)}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[860px]">
                {/* Hour ruler */}
                <div className="grid border-b border-zinc-200 dark:border-zinc-800 text-[10px] text-zinc-500 dark:text-zinc-400" style={{ gridTemplateColumns: "180px repeat(24, 1fr) 30px" }}>
                  <div className="px-3 py-1 border-r border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/40">Officer</div>
                  {HOURS.slice(0, 24).map((h) => (
                    <div key={h} className="text-center py-1 border-r border-zinc-100 dark:border-zinc-800 tabular-nums">
                      {h}
                    </div>
                  ))}
                  <div></div>
                </div>
                {employees.map((emp) => {
                  const ents = (roster.entries || []).filter((e) => e.employee_id === emp.employee_id && e.date === ds);
                  return (
                    <div
                      key={emp.employee_id}
                      className="grid border-b border-zinc-100 dark:border-zinc-800/60 hover:bg-zinc-50/50 dark:hover:bg-zinc-800/20"
                      style={{ gridTemplateColumns: "180px 1fr 30px" }}
                    >
                      <div className="px-3 py-2 text-xs font-medium text-zinc-700 dark:text-zinc-200 border-r border-zinc-200 dark:border-zinc-800 truncate">
                        {emp.name}
                      </div>
                      <div className="relative h-8" data-testid={`tl-row-${emp.employee_id}-${ds}`}>
                        {/* hour grid lines */}
                        <div
                          className="absolute inset-0 grid"
                          style={{ gridTemplateColumns: "repeat(24, 1fr)", pointerEvents: "none" }}
                        >
                          {HOURS.slice(0, 24).map((h) => (
                            <div key={h} className="border-r border-zinc-100 dark:border-zinc-800/40" />
                          ))}
                        </div>
                        {ents.map((en) => {
                          const sh = shiftMap[en.shift_id];
                          if (!sh) return null;
                          const startM = minutesOfDay(sh.start_time);
                          let endM = minutesOfDay(sh.end_time);
                          if (endM <= startM) endM += 24 * 60; // overnight shift → wrap
                          const left = (startM / (24 * 60)) * 100;
                          const width = ((endM - startM) / (24 * 60)) * 100;
                          return (
                            <div
                              key={en.entry_id}
                              className="absolute top-1 bottom-1 rounded text-[10px] font-medium px-1.5 flex items-center gap-1 truncate"
                              style={{
                                left: `${left}%`,
                                width: `${Math.min(width, 100 - left)}%`,
                                backgroundColor: (sh.color || "#002FA7") + "33",
                                borderLeft: `3px solid ${sh.color || "#002FA7"}`,
                                color: "#18181b",
                              }}
                              title={`${sh.name} ${fmtTime(sh.start_time)}–${fmtTime(sh.end_time)}`}
                              data-testid={`tl-bar-${en.entry_id}`}
                            >
                              {en.is_locked && <Lock className="w-2.5 h-2.5 flex-shrink-0" />}
                              <span className="truncate">{sh.name}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};


/* ----------------------------------------------------------
 * Coverage Heatmap view
 * Grid of shifts × days. Each cell shows headcount and is
 * colour-coded vs the shift's min_staff/max_staff:
 *   below min → red, at min → amber, between min..max → green,
 *   above max → blue (over-staffed).
 * ---------------------------------------------------------- */
export const HeatmapRosterView = ({ roster, employees, shifts }) => {
  if (!roster?.start_date || !roster?.end_date) return null;
  const dates = eachDayOfInterval({
    start: parseISO(roster.start_date),
    end: parseISO(roster.end_date),
  });

  const shiftCount = (shift_id, date_str) =>
    (roster.entries || []).filter((e) => e.shift_id === shift_id && e.date === date_str).length;

  const cellTone = (count, sh) => {
    const min = Math.max(1, sh.min_staff || 1);
    const max = sh.max_staff || (employees.length || 99);
    if (count === 0) return { bg: "#FEE2E2", text: "#991B1B", label: "Uncovered" };
    if (count < min) return { bg: "#FECACA", text: "#7F1D1D", label: "Under-staffed" };
    if (count === min) return { bg: "#FEF3C7", text: "#92400E", label: "At minimum" };
    if (count <= max) return { bg: "#D1FAE5", text: "#065F46", label: "Healthy" };
    return { bg: "#DBEAFE", text: "#1E3A8A", label: "Over-staffed" };
  };

  return (
    <div className="border border-zinc-200 dark:border-zinc-800 rounded overflow-x-auto" data-testid="layout-heatmap-root">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-zinc-900 text-white">
            <th className="text-left px-3 py-2 sticky left-0 z-10 bg-zinc-900 min-w-[180px]">Shift</th>
            {dates.map((d) => (
              <th
                key={format(d, "yyyy-MM-dd")}
                className={`text-center px-1.5 py-2 font-medium ${
                  d.getDay() === 0 || d.getDay() === 6 ? "bg-zinc-800" : ""
                }`}
              >
                <div className="text-[9px] uppercase">{format(d, "EEE")}</div>
                <div className="tabular-nums">{format(d, "d")}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shifts.map((sh) => (
            <tr key={sh.shift_id} className="border-t border-zinc-200 dark:border-zinc-800" data-testid={`hm-row-${sh.shift_id}`}>
              <td className="px-3 py-2 sticky left-0 z-10 bg-white dark:bg-zinc-900 border-r border-zinc-200 dark:border-zinc-800">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-6 rounded" style={{ backgroundColor: sh.color || "#002FA7" }} />
                  <div>
                    <div className="font-semibold text-zinc-900 dark:text-zinc-100">{sh.name}</div>
                    <div className="text-[10px] text-zinc-500 tabular-nums">
                      {fmtTime(sh.start_time)}–{fmtTime(sh.end_time)} · min {sh.min_staff || 1}{sh.max_staff ? ` · max ${sh.max_staff}` : ""}
                    </div>
                  </div>
                </div>
              </td>
              {dates.map((d) => {
                const ds = format(d, "yyyy-MM-dd");
                const c = shiftCount(sh.shift_id, ds);
                const tone = cellTone(c, sh);
                return (
                  <td
                    key={ds}
                    className="text-center px-1.5 py-2 text-[12px] font-bold tabular-nums border border-zinc-100 dark:border-zinc-800/60"
                    style={{ backgroundColor: tone.bg, color: tone.text }}
                    title={`${tone.label}: ${c} assigned`}
                    data-testid={`hm-cell-${sh.shift_id}-${ds}`}
                  >
                    {c}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-3 px-3 py-2 border-t border-zinc-200 dark:border-zinc-800 text-[11px] text-zinc-600 dark:text-zinc-400 bg-zinc-50 dark:bg-zinc-800/40 flex-wrap">
        <span className="font-semibold uppercase tracking-wide">Legend:</span>
        <Swatch bg="#FEE2E2" text="#991B1B" label="Uncovered (0)" />
        <Swatch bg="#FECACA" text="#7F1D1D" label="Under (< min)" />
        <Swatch bg="#FEF3C7" text="#92400E" label="At min" />
        <Swatch bg="#D1FAE5" text="#065F46" label="Healthy" />
        <Swatch bg="#DBEAFE" text="#1E3A8A" label="Over max" />
      </div>
    </div>
  );
};

const Swatch = ({ bg, text, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="inline-block w-4 h-4 rounded border" style={{ backgroundColor: bg, color: text }} />
    {label}
  </span>
);


/* ----------------------------------------------------------
 * Day Cards view
 * One day at a time, kanban-style — each shift becomes a
 * column showing the cards of employees rostered on it.
 * ---------------------------------------------------------- */
export const DayCardsRosterView = ({ roster, employees, shifts, selectedDate, onSelectDate }) => {
  if (!roster?.start_date || !roster?.end_date) return null;
  const dates = eachDayOfInterval({
    start: parseISO(roster.start_date),
    end: parseISO(roster.end_date),
  });
  const ds = selectedDate || format(dates[0], "yyyy-MM-dd");
  const empMap = Object.fromEntries(employees.map((e) => [e.employee_id, e]));

  return (
    <div className="space-y-4" data-testid="layout-daycards-root">
      {/* Day picker strip */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="dc-date-strip">
        {dates.map((d) => {
          const dStr = format(d, "yyyy-MM-dd");
          const active = dStr === ds;
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          return (
            <button
              key={dStr}
              onClick={() => onSelectDate?.(dStr)}
              className={`flex-shrink-0 w-16 py-2 rounded text-center transition-colors ${
                active
                  ? "bg-[#002FA7] text-white"
                  : isWeekend
                    ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
                    : "bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-800"
              }`}
              data-testid={`dc-date-${dStr}`}
            >
              <div className="text-[9px] uppercase font-bold">{format(d, "EEE")}</div>
              <div className="text-base font-bold tabular-nums leading-tight">{format(d, "d")}</div>
              <div className="text-[9px] tabular-nums">{format(d, "MMM")}</div>
            </button>
          );
        })}
      </div>

      {/* Shift columns */}
      <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
        {shifts.map((sh) => {
          const ents = (roster.entries || []).filter((e) => e.date === ds && e.shift_id === sh.shift_id);
          const min = sh.min_staff || 1;
          const max = sh.max_staff;
          const understaffed = ents.length < min;
          return (
            <div
              key={sh.shift_id}
              className="rounded border bg-white dark:bg-zinc-900 overflow-hidden"
              style={{ borderTop: `4px solid ${sh.color || "#002FA7"}` }}
              data-testid={`dc-col-${sh.shift_id}`}
            >
              <div className="px-3 py-2 border-b border-zinc-200 dark:border-zinc-800">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                    {sh.name}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                      understaffed
                        ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                        : "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                    }`}
                  >
                    {ents.length}{max ? ` / ${max}` : ""}
                  </span>
                </div>
                <p className="text-[11px] text-zinc-500 dark:text-zinc-400 tabular-nums">
                  {fmtTime(sh.start_time)}–{fmtTime(sh.end_time)}
                </p>
              </div>
              <div className="p-2 space-y-1.5 min-h-[120px]">
                {ents.length === 0 ? (
                  <p className="text-[11px] text-zinc-400 dark:text-zinc-500 italic text-center py-4">
                    No-one assigned · need {min}
                  </p>
                ) : (
                  ents.map((en) => {
                    const emp = empMap[en.employee_id];
                    if (!emp) return null;
                    return (
                      <div
                        key={en.entry_id}
                        className="flex items-center gap-2 px-2 py-1.5 bg-zinc-50 dark:bg-zinc-800/50 rounded text-xs"
                        data-testid={`dc-card-${en.entry_id}`}
                      >
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: sh.color || "#002FA7" }}
                        >
                          {emp.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                        </div>
                        <span className="flex-1 truncate text-zinc-900 dark:text-zinc-100">
                          {emp.name}
                        </span>
                        {en.is_locked && <Lock className="w-3 h-3 text-zinc-500" />}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
